import { useCallback, useEffect } from 'react';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { Button, InlineLoading } from '@carbon/react';
import {
  ChevronLeft,
  ChevronRight,
  Close,
  TrashCan,
  Renew,
  ResetAlt,
  Information,
  Edit,
} from '@carbon/icons-react';
import {
  tcFilteredUnitsAtom,
  tcSelectedDevAreaAtom,
  tcSelectDevAreaRequestAtom,
  unitOverridesAtom,
  tcUnitResolutionPendingAtom,
  tcUnitRotationPendingAtom,
  unitAzimuthOverridesAtom,
  tcUnitShapeEditRequestedAtom,
} from 'pages/type-curves/type-curve-filters/atoms';
import {
  useUnitOverridesQuery,
  useCreateUnitOverrideMutation,
  useUndoLastRemovalMutation,
} from 'pages/type-curves/use-unit-overrides';
import { activeTcWorkspaceAtom, tcDetailsRightPanelAtom } from '../atoms';
import { useUnitRotation } from './use-unit-rotation';
import { toastAtom } from 'pages/type-curves/atoms';
import styles from './unit-resolution-toolbar.module.scss';

// Toolbar above the map. Supersedes TcUnitNavigator: prev/next render
// everywhere; rotate / edit / bin / info + the "X removed" status only appear
// inside an active, non-promoted workspace. Mirrors the mock's MapPanel unit
// toolbar. Removal/rotation persist as overrides; the refresh fan-out keeps the
// map (client-side hide), status, and overlap count in sync. Edit is local to
// the session — the shape editor has no persistence endpoint yet.
export const UnitResolutionToolbar = () => {
  const units = useAtomValue(tcFilteredUnitsAtom);
  const [selected, setSelected] = useAtom(tcSelectedDevAreaAtom);
  const requestSelect = useSetAtom(tcSelectDevAreaRequestAtom);
  const overrides = useAtomValue(unitOverridesAtom);
  // Remove/undo (resolution) and rotate/un-rotate use separate pending atoms
  // but the toolbar shows one identical loader + disabled state for either.
  const resolutionPending = useAtomValue(tcUnitResolutionPendingAtom);
  const rotationPending = useAtomValue(tcUnitRotationPendingAtom);
  const pending = resolutionPending || rotationPending;
  const workspace = useAtomValue(activeTcWorkspaceAtom);
  const setRightPanel = useSetAtom(tcDetailsRightPanelAtom);
  const setToast = useSetAtom(toastAtom);
  const setAzimuthOverrides = useSetAtom(unitAzimuthOverridesAtom);
  const [editingShape, setEditingShape] = useAtom(tcUnitShapeEditRequestedAtom);

  const workspaceId = workspace?.id ?? null;
  const inWorkspace = workspaceId !== null;
  // Promotion is the only thing that closes editing. A verified unit-resolution verdict used to
  // lock Undo as well, which left a workspace cloned from a previous version unable to resolve its
  // own overlaps: the clone copies the source's verdict, so the lock was already on before the
  // operator touched anything.
  const canEdit = inWorkspace && !workspace?.promoted;

  // Drop the stated bearings on load / workspace switch — a fresh page draws
  // the backend's baked-in tile angle; only this session's statements turn it.
  useEffect(() => {
    setAzimuthOverrides(new Map());
  }, [workspaceId, setAzimuthOverrides]);

  // Editing is scoped to one unit; switching or clearing the selection ends it
  // (and discards the working copy, since nothing is persisted yet).
  const selectedUnitName = selected?.developmentAreaName ?? null;
  useEffect(() => {
    setEditingShape(false);
  }, [selectedUnitName, workspaceId, setEditingShape]);

  useUnitOverridesQuery(workspace);
  const createOverride = useCreateUnitOverrideMutation(workspace);
  const undoLastRemoval = useUndoLastRemovalMutation(workspace);

  const count = units.length;
  const currentIdx = selected
    ? units.findIndex((u) => u.name === selected.developmentAreaName)
    : -1;

  const selectedId = selected?.developmentAreaId ?? null;
  const removedCount = overrides.filter((o) => o.action === 'remove').length;
  const rotation = useUnitRotation();
  const isRotated = rotation.isRotated;

  const unitLabel = selected
    ? `${selected.developmentAreaName}${
        currentIdx >= 0 ? ` (${currentIdx + 1}/${count})` : ''
      }`
    : `No unit selected (${count} available)`;

  const goToPrev = useCallback(() => {
    if (currentIdx <= 0 || count === 0) return;
    requestSelect(units[currentIdx - 1].name);
  }, [currentIdx, count, units, requestSelect]);

  const goToNext = useCallback(() => {
    if (count === 0) return;
    if (currentIdx < 0) {
      requestSelect(units[0].name);
      return;
    }
    if (currentIdx >= count - 1) return;
    requestSelect(units[currentIdx + 1].name);
  }, [currentIdx, count, units, requestSelect]);

  const handleBin = useCallback(async () => {
    if (selectedId === null) return;
    const result = await createOverride({
      unit_id: selectedId,
      action: 'remove',
    });
    if (!result.ok) {
      setToast({
        kind: 'error',
        title: 'Failed to remove unit',
        subtitle: result.message,
      });
    }
  }, [selectedId, createOverride, setToast]);

  const handleRotate = rotation.toggle;

  return (
    <div
      className={styles.unitToolbar}
      role="toolbar"
      aria-label="Unit toolbar"
    >
      <span className={styles.toolbarLabel} aria-hidden="true">
        Unit
      </span>
      <span className={styles.toolbarDivider} aria-hidden="true" />
      <Button
        kind="ghost"
        size="sm"
        hasIconOnly
        iconDescription="Previous unit"
        tooltipPosition="bottom"
        renderIcon={ChevronLeft}
        disabled={!selected || currentIdx <= 0}
        onClick={goToPrev}
      />
      <span className={styles.toolbarText} title={unitLabel}>
        {unitLabel}
      </span>
      <Button
        kind="ghost"
        size="sm"
        hasIconOnly
        iconDescription="Next unit"
        tooltipPosition="bottom"
        renderIcon={ChevronRight}
        disabled={count === 0 || currentIdx >= count - 1}
        onClick={goToNext}
      />
      <span className={styles.toolbarDivider} aria-hidden="true" />
      <Button
        kind="ghost"
        size="sm"
        hasIconOnly
        iconDescription="Clear selection"
        tooltipPosition="bottom"
        renderIcon={Close}
        disabled={!selected}
        onClick={() => setSelected(null)}
      />

      {canEdit && (
        <>
          <span className={styles.toolbarDivider} aria-hidden="true" />
          <Button
            kind="ghost"
            size="sm"
            hasIconOnly
            iconDescription={
              isRotated
                ? 'Restore auto-detected LTD Geometry direction'
                : 'Rotate LTD Geometry direction 90°'
            }
            tooltipPosition="bottom"
            renderIcon={Renew}
            disabled={!selected || pending || editingShape}
            isSelected={isRotated}
            onClick={handleRotate}
          />
          <Button
            kind="ghost"
            size="sm"
            hasIconOnly
            iconDescription={
              editingShape ? 'Finish editing unit shape' : 'Edit unit shape'
            }
            tooltipPosition="bottom"
            renderIcon={Edit}
            disabled={!selected || pending}
            isSelected={editingShape}
            onClick={() => setEditingShape((on) => !on)}
          />
          <Button
            kind="ghost"
            size="sm"
            hasIconOnly
            iconDescription="Remove unit"
            tooltipPosition="bottom"
            renderIcon={TrashCan}
            disabled={!selected || pending || editingShape}
            onClick={handleBin}
          />
        </>
      )}

      {inWorkspace && (
        <>
          <span className={styles.toolbarDivider} aria-hidden="true" />
          <Button
            kind="ghost"
            size="sm"
            hasIconOnly
            iconDescription="Overlap resolution info"
            tooltipPosition="bottom"
            renderIcon={Information}
            onClick={() =>
              setRightPanel({ type: 'inventory-guide', open: true })
            }
          />
        </>
      )}

      {inWorkspace && pending && (
        <>
          <span className={styles.toolbarDivider} aria-hidden="true" />
          <InlineLoading
            className={styles.toolbarLoading}
            description="Updating…"
          />
        </>
      )}

      {canEdit && !pending && removedCount > 0 && (
        <>
          <span className={styles.toolbarDivider} aria-hidden="true" />
          <span className={styles.toolbarStatus} aria-live="polite">
            {removedCount === 1
              ? '1 Unit Removed from Type Curve'
              : `${removedCount} Units Removed from Type Curve`}
          </span>
          <Button
            kind="ghost"
            size="sm"
            renderIcon={ResetAlt}
            onClick={async () => {
              const result = await undoLastRemoval();
              if (!result.ok) {
                setToast({
                  kind: 'error',
                  title: 'Failed to undo removal',
                  subtitle: result.message,
                });
              }
            }}
          >
            Undo
          </Button>
        </>
      )}
    </div>
  );
};

UnitResolutionToolbar.displayName = 'UnitResolutionToolbar';
